import React, { useState, useEffect, useCallback, useRef } from "react";
import "./App.scss";
import { Box, CssBaseline, ThemeProvider } from "@mui/material";
import Header from "./components/Header";
import MapComponent from "./components/MapContainer";
import SearchOptions from "./components/SearchOption";
import StationDetails from "./components/StationDetails";
import {
  getPromotionLinks,
  getStationsImagesData,
} from "./store/actions/stationActions";
import { useDispatch } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PrintableRoutes from "./components/PrintableRoutes";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import TripPlanner from "./components/TripPlanner";
import { MAP_VIEWS } from "./store/constants/mapViewConstants";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Footer from "./components/Footer";
import CookieDisclaimer from "./components/CookieDisclaimer";
import ThemeRegistry from "./components/ThemeRegistry";
import { clearLocalStorage, fetchPaymentMethods } from "./lib/helper";
import { DEFAULT_FILTER, DEFAULT_LOCATION } from "./store/constants";

function App() {
  const dispatch = useDispatch();
  const [mapCenter, setMapCenter] = useState(DEFAULT_LOCATION?.position);
  const [selectedStation, setSelectedStation] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [filterValues, setFilterValues] = useState(DEFAULT_FILTER);

  const [position, setPosition] = useState(DEFAULT_LOCATION?.position);
  const [sourceCity, setSourceCity] = useState(
    JSON.parse(window?.localStorage?.getItem("SOURCE_CITY") || JSON.stringify({
      name: DEFAULT_LOCATION?.locationName,
      ...DEFAULT_LOCATION.position,
    }))
  );
  const [destinationCity, setDestinationCity] = useState(
    JSON.parse(window?.localStorage?.getItem("DESTINATION_CITY") || `{}`)
  );
  const [routeDetails, setRouteDetails] = useState();
  const [destinations, setDestinations] = useState(
    JSON.parse(window?.localStorage?.getItem("DESTINATIONS") || `[]`)
  );
  const [mapView, setMapView] = useState(MAP_VIEWS.normal);
  const [isTrafficViewEnabled, setIsTrafficViewEnabled] = useState(false);

  const [tripPlannerOpen, setTripPlannerOpen] = useState(false);
  const [buildTrip, setBuildTrip] = useState(0);
  const [clearTrip, setClearTrip] = useState(0);
  const [isCurrentLocation, setIsCurrentLocation] = useState(false);
  const [locationPermissionDenied, setLocationPermissionDenied] = useState(false);
  const locationPermissionDeniedRef = useRef(false);
  console.log("🚀 ~ App ~ isCurrentLocation:", isCurrentLocation);

  const handleGetDirectionsClick = (station) => {
    if (destinationCity || destinations?.length) {
      return toast.warn(
        "You cannot get directed instead you can add this to your trip."
      );
    }
    
    // Set starting point to user's current location (without moving map)
    getCurrentLocationAndShow(false);
    
    const fullAddress = `${station?.Street || ""}, ${station?.StationCity || ""}, ${station?.StationState || ""}`.replace(/^,\s*|,\s*$/g, '').trim();
    setDestinationCity({
      lat: station?.Latitude,
      lng: station?.Longitude,
      name: fullAddress || `${station?.StationCity}, ${station?.StationState}`,
    });
    setTripPlannerOpen(true);
  };

  const handleLocationSelect = (location) => {
    setMapCenter(location); // Set center but don't reset zoom
    setPosition((prevPosition) => {
      // Only update if the new position is different
      if (
        !prevPosition ||
        prevPosition?.lat !== location?.lat ||
        prevPosition?.lng !== location?.lng
      ) {
        return location;
      }
      return prevPosition;
    });
  };

  const handleStationSelect = (station) => {
    setPosition(null);
    setSelectedStation(station);
  };

  const handleOpenModal = (station) => {
    setOpenModal(true);
  };

  const handleClearTrip = () => {
    if (window?.localStorage) {
      clearLocalStorage();
    }
    setDestinationCity(null);
    setSourceCity(null);
    setRouteDetails(null);
    setDestinations([]);

    setMapCenter(null);
    setPosition(null);
  };

  const handleShowLocation = () => {
    if (navigator.geolocation) {
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        if (destinationCity && sourceCity) {
          handleClearTrip();
        }
        setTripPlannerOpen(false);
        if (result.state === "denied") {
          if (!locationPermissionDenied) {
            toast.error("Please enable location services to use this feature.");
            setLocationPermissionDenied(true);
          }
          // Use default location when denied
          setSourceCity({
            name: DEFAULT_LOCATION?.locationName,
            ...DEFAULT_LOCATION.position,
          });
          setMapCenter(DEFAULT_LOCATION.position);
          setPosition(DEFAULT_LOCATION.position);
          setIsCurrentLocation(false);
        } else {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              const { latitude, longitude } = pos.coords;
              const userLocation = { lat: latitude, lng: longitude };
              setSourceCity(userLocation);
              setMapCenter(userLocation);
              setIsCurrentLocation(true);
              setPosition(userLocation);
              setLocationPermissionDenied(false);
            },
            () => {
              toast.error("Unable to retrieve location.");
              // Use default location on error
              setSourceCity({
                name: DEFAULT_LOCATION?.locationName,
                ...DEFAULT_LOCATION.position,
              });
              setMapCenter(DEFAULT_LOCATION.position);
              setPosition(DEFAULT_LOCATION.position);
              setIsCurrentLocation(false);
            },
            {
              enableHighAccuracy: true,
              timeout: 5000,
              maximumAge: 60000,
            }
          );
        }
      });
    } else {
      toast.error("Geolocation is not supported by your browser.");
    }
  };

  const handleTripPlanner = async () => {
    try {
      if (!tripPlannerOpen) {
        setMapCenter(null);
        setPosition(null);
      }
      setTripPlannerOpen(!tripPlannerOpen);
    } catch (err) {
      console.error("handleTripPlanner ~ err:", err);
    }
  };

  const getLocationName = async (latitude, longitude) => {
    try {
      // Add a small delay to respect rate limits
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`,
        {
          headers: {
            'User-Agent': 'StationLocator/1.0'
          }
        }
      );
      
      if (!response.ok) {
        console.warn(`HTTP error! status: ${response.status}`);
        return {
          lat: latitude,
          lng: longitude,
          name: `Location (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`,
        };
      }

      const data = await response.json();
      if (data?.display_name) {
        return {
          lat: latitude,
          lng: longitude,
          name: data.display_name,
        };
      } else {
        console.warn("No display name returned from geocoding service");
        return {
          lat: latitude,
          lng: longitude,
          name: `Location (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`,
        };
      }
    } catch (error) {
      console.error("Error fetching location name:", error);
      return {
        lat: latitude,
        lng: longitude,
        name: `Location (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`,
      };
    }
  };

  const checkLocationPermission = useCallback(() => {
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        if (result.state === 'denied' && !locationPermissionDeniedRef.current) {
          toast.error("Please enable location access in browser settings.");
          setLocationPermissionDenied(true);
          locationPermissionDeniedRef.current = true;
        } else if (result.state === 'granted') {
          setLocationPermissionDenied(false);
          locationPermissionDeniedRef.current = false;
        }
      });
    }
  }, []);

  const getCurrentLocationAndShow = useCallback(async (moveMap = true) => {
    try {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          // Don't show error toast immediately, just use default location
          resolve(DEFAULT_LOCATION.position);
          return;
        }

        // Check permission first to avoid unnecessary requests
        navigator.permissions?.query({ name: 'geolocation' }).then((result) => {
          if (result.state === 'denied') {
            resolve(DEFAULT_LOCATION.position);
            return;
          }

          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const { latitude, longitude } = position.coords;

              try {
                const locationWithName = await getLocationName(
                  latitude,
                  longitude
                );

                if (locationWithName?.lat && locationWithName?.lng) {
                  setSourceCity(locationWithName);
                  if (moveMap) {
                    setPosition(locationWithName);
                    setMapCenter(locationWithName);
                  }
                  setIsCurrentLocation(true);
                  setLocationPermissionDenied(false);
                  resolve(locationWithName);
                } else {
                  // Use default location silently on error
                  resolve(DEFAULT_LOCATION.position);
                }
              } catch (error) {
                console.error("Error fetching location name:", error);
                // Use default location silently on error
                resolve(DEFAULT_LOCATION.position);
              }
            },
            (error) => {
              console.error("Error getting location:", error);
              switch (error.code) {
                case error.PERMISSION_DENIED:
                  // Use default location when permission is denied
                  resolve(DEFAULT_LOCATION.position);
                  break;
                case error.POSITION_UNAVAILABLE:
                  // Use default location when position is unavailable
                  resolve(DEFAULT_LOCATION.position);
                  break;
                case error.TIMEOUT:
                  // Use default location on timeout without showing error
                  resolve(DEFAULT_LOCATION.position);
                  break;
                default:
                  // Use default location on unknown error
                  resolve(DEFAULT_LOCATION.position);
                  break;
              }
            },
            {
              enableHighAccuracy: true, // Enable high accuracy for better results
              timeout: 3000, // Shorter timeout for faster response
              maximumAge: 60000, // Allow cached location for 1 minute
            }
          );
        }).catch(() => {
          // If permissions API is not supported, try direct geolocation
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const { latitude, longitude } = position.coords;
              const locationWithName = await getLocationName(latitude, longitude);
              if (locationWithName?.lat && locationWithName?.lng) {
                setSourceCity(locationWithName);
                if (moveMap) {
                  setPosition(locationWithName);
                  setMapCenter(locationWithName);
                }
                setIsCurrentLocation(true);
                setLocationPermissionDenied(false);
                resolve(locationWithName);
              } else {
                resolve(DEFAULT_LOCATION.position);
              }
            },
            () => {
              resolve(DEFAULT_LOCATION.position);
            },
            {
              enableHighAccuracy: true,
              timeout: 3000,
              maximumAge: 60000,
            }
          );
        });
      });
    } catch (err) {
      console.error("🚀 ~ getCurrentLocationAndShow ~ err:", err);
      // Use default location on any error
      return DEFAULT_LOCATION.position;
    }
  }, []);

  const handleAddToTrip = async (station) => {
    try {
      let currentSourceCity = sourceCity; // Use a local variable for sourceCity

      // If sourceCity isn't set, try to get the current location
      if (!currentSourceCity?.name) {
        try {
          currentSourceCity = await getCurrentLocationAndShow(); // Fetch and store location in local variable
        } catch (locationError) {
          toast.error(
            "Unable to retrieve current location. Please enable location services."
          );
          return;
        }
      }

      // If destinationCity isn't set, set the station as the destination
      if (
        !destinationCity ||
        (destinationCity && Object.keys(destinationCity).length === 0)
      ) {
        const fullAddress = `${station?.Street || ""}, ${station?.StationCity || ""}, ${station?.StationState || ""}`.replace(/^,\s*|,\s*$/g, '').trim();
        setDestinationCity({
          lat: station?.Latitude,
          lng: station?.Longitude,
          name: fullAddress || `${station?.StationCity}, ${station?.StationState}`,
        });

        toast.success(`${station?.StationCity}, ${station?.StationState} set as destination!`);
        setTripPlannerOpen(true); // Open Trip Planner after setting destination
        return;
      }

      // If both source and destination are set, add station as an intermediate destination
      const alreadyAdded = destinations.some(
        (dest) =>
          dest.lat === station?.Latitude && dest.lng === station?.Longitude
      );

      if (alreadyAdded) {
        toast.warn("This station is already in your trip.");
        return;
      }

      const fullAddress = `${station?.Street || ""}, ${station?.StationCity || ""}, ${station?.StationState || ""}`.replace(/^,\s*|,\s*$/g, '').trim();
      setDestinations((prevDestinations) => [
        ...prevDestinations,
        {
          lat: station?.Latitude,
          lng: station?.Longitude,
          name: fullAddress || `${station?.StationCity}, ${station?.StationState}`,
        },
      ]);

      setTripPlannerOpen(true); // Open Trip Planner if adding intermediate stops
      
      // Automatically build the trip if source and destination are already set
      if (currentSourceCity?.name && destinationCity?.name) {
        setBuildTrip((prev) => prev + 1); // Trigger automatic trip building
      }
    } catch (error) {
      console.error("handleAddToTrip ~ error:", error);
      toast.error("Failed to add station to the trip. Please try again.");
    }
  };

  useEffect(() => {
    dispatch(getStationsImagesData());

    // Initialize payment methods from API
    fetchPaymentMethods().catch((error) => {
      console.error("Failed to initialize payment methods:", error);
    });

    // Check location permission immediately and show toast if denied
    checkLocationPermission();

    // Try to get current location immediately
    getCurrentLocationAndShow();

    // Listen for permission changes to avoid reload requirement
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        result.addEventListener('change', () => {
          if (result.state === 'granted') {
            // Permission granted, get current location
            setLocationPermissionDenied(false);
            getCurrentLocationAndShow();
          } else if (result.state === 'denied') {
            // Permission denied, use default location
            setSourceCity({
              name: DEFAULT_LOCATION?.locationName,
              ...DEFAULT_LOCATION.position,
            });
            setPosition(DEFAULT_LOCATION.position);
            setMapCenter(DEFAULT_LOCATION.position);
            setIsCurrentLocation(false);
            if (!locationPermissionDeniedRef.current) {
              toast.error("Please enable location access in browser settings.");
              setLocationPermissionDenied(true);
              locationPermissionDeniedRef.current = true;
            }
          }
        });
      });
    }
  }, [dispatch, getCurrentLocationAndShow, checkLocationPermission]);

  useEffect(() => {
    dispatch(getPromotionLinks());
  }, [dispatch]);

  useEffect(() => {
    if (destinations?.length && window.localStorage) {
      window?.localStorage?.setItem(
        "DESTINATIONS",
        JSON.stringify(destinations)
      );
    }

    if (sourceCity && window?.localStorage) {
      window?.localStorage?.setItem("SOURCE_CITY", JSON.stringify(sourceCity));
    }
    if (destinationCity && window?.localStorage) {
      window?.localStorage?.setItem(
        "DESTINATION_CITY",
        JSON.stringify(destinationCity)
      );
    }
  }, [destinations, sourceCity, destinationCity]);
  return (
    <ThemeProvider theme={ThemeRegistry}>
      {/* <DirectionsList source={sourceCity} destination={destinationCity} /> */}
      <CssBaseline />
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              <Box className="App">
                <ToastContainer />
                <Header
                  setIsCurrentLocation={setIsCurrentLocation}
                  mapCenter={mapCenter}
                  onLocationSelect={handleLocationSelect}
                  handleShowLocation={handleShowLocation}
                  setSourceCity={setSourceCity}
                  setDestinationCity={setDestinationCity}
                  setRouteDetails={setRouteDetails}
                  handleTripPlanner={handleTripPlanner}
                  setMapView={setMapView}
                  tripPlannerOpen={tripPlannerOpen}
                  isTrafficViewEnabled={isTrafficViewEnabled}
                  setIsTrafficViewEnabled={setIsTrafficViewEnabled}
                  position={position}
                  setPosition={setPosition}
                />
                <Box className="map-section">
                  <MapComponent
                    isCurrentLocation={isCurrentLocation}
                    filterValues={filterValues}
                    center={mapCenter}
                    onStationSelect={handleStationSelect}
                    onOpenModal={handleOpenModal}
                    position={position}
                    handleShowLocation={handleShowLocation}
                    sourceCity={sourceCity}
                    destinationCity={destinationCity}
                    setRouteDetails={setRouteDetails}
                    routeDetails={routeDetails}
                    setSourceCity={setSourceCity}
                    destinations={destinations}
                    mapView={mapView}
                    buildTrip={buildTrip}
                    clearTrip={clearTrip}
                    setClearTrip={setClearTrip}
                    isTrafficViewEnabled={isTrafficViewEnabled}
                  />
                  <SearchOptions
                    setPosition={setPosition}
                    setSourceCity={setSourceCity}
                    filterValues={filterValues}
                    setFilterValues={setFilterValues}
                    routeDetails={routeDetails}
                  />

                  {tripPlannerOpen && (
                    <TripPlanner
                      sourceCity={sourceCity}
                      destinationCity={destinationCity}
                      setDestinationCity={setDestinationCity}
                      setSourceCity={setSourceCity}
                      onClose={() => {
                        setTripPlannerOpen(false);
                      }}
                      destinations={destinations}
                      setRouteDetails={setRouteDetails}
                      routeDetails={routeDetails}
                      setDestinations={setDestinations}
                      setBuildTrip={setBuildTrip}
                      setClearTrip={setClearTrip}
                    />
                  )}
                  <StationDetails
                    station={selectedStation}
                    isOpenModal={openModal}
                    setOpenModal={setOpenModal}
                    onGetDirectionsClick={handleGetDirectionsClick}
                    handleAddToTrip={handleAddToTrip}
                  />
                </Box>
                <Footer />
                {/* <CookieDisclaimer /> */}
              </Box>
            }
          />
          <Route path="/printable-routes" element={<PrintableRoutes />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
